(function () {
  const preloader = document.querySelector("[data-preloader]");
  const header = document.querySelector("[data-header]");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
  const compactViewport = window.matchMedia("(max-width: 989px)").matches;
  const wishlistStorageKey = "bouquetLilleWishlist";

  function initSmoothScroll() {
    if (reduceMotion || coarsePointer || compactViewport || window.Shopify?.designMode || !window.Lenis) return null;

    const lenis = new window.Lenis({
      duration: 1.12,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      gestureOrientation: "vertical",
      orientation: "vertical",
      smoothWheel: true,
      syncTouch: false,
      wheelMultiplier: 0.92,
      prevent: (node) => {
        const target = node instanceof Element ? node : node?.parentElement;
        return Boolean(
          target?.closest(
            "[data-lenis-prevent], cart-drawer, .cart-drawer, .drawer, modal-dialog, details-modal, .predictive-search, .menu-drawer, .bouquet-featured-home__rail"
          )
        );
      },
    });

    window.bouquetLenis = lenis;

    if (window.gsap && window.ScrollTrigger) {
      window.gsap.registerPlugin(window.ScrollTrigger);
      lenis.on("scroll", window.ScrollTrigger.update);
      window.gsap.ticker.add((time) => lenis.raf(time * 1000));
      window.gsap.ticker.lagSmoothing(0);
    } else {
      const raf = (time) => {
        lenis.raf(time);
        window.requestAnimationFrame(raf);
      };

      window.requestAnimationFrame(raf);
    }

    window.addEventListener("load", () => window.ScrollTrigger?.refresh(), { once: true });

    return lenis;
  }

  function safeWishlistUrl(value, fallback = "/collections/all") {
    if (!value) return fallback;

    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return fallback;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
      return fallback;
    }
  }

  function safeWishlistImage(value) {
    if (!value) return "";

    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol !== "http:" && url.protocol !== "https:") return "";
      return url.href;
    } catch (error) {
      return "";
    }
  }

  function normalizeWishlistItem(item) {
    if (!item || typeof item !== "object") return null;

    const id = String(item.id || item.handle || "").trim();
    const title = String(item.title || "Bouquet").trim().slice(0, 120);

    if (!id || !title) return null;

    return {
      id,
      handle: String(item.handle || "").trim().slice(0, 90),
      title,
      url: safeWishlistUrl(item.url),
      image: safeWishlistImage(item.image),
      price: String(item.price || "").trim().slice(0, 60),
    };
  }

  function readWishlist() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(wishlistStorageKey) || "[]");
      if (!Array.isArray(parsed)) return [];
      return parsed.map(normalizeWishlistItem).filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  function writeWishlist(items) {
    try {
      window.localStorage.setItem(wishlistStorageKey, JSON.stringify(items.slice(0, 80)));
    } catch (error) {
      // Storage can be blocked in strict private modes; keep UI usable.
    }
  }

  function getWishlistButtonItem(button) {
    return normalizeWishlistItem({
      id: button.dataset.productId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      price: button.dataset.productPrice,
    });
  }

  function removeWishlistItem(id) {
    const nextItems = readWishlist().filter((item) => item.id !== id);
    writeWishlist(nextItems);
    syncWishlistUi();
  }

  function toggleWishlistItem(item) {
    const items = readWishlist();
    const exists = items.some((savedItem) => savedItem.id === item.id);
    const nextItems = exists
      ? items.filter((savedItem) => savedItem.id !== item.id)
      : [item, ...items.filter((savedItem) => savedItem.id !== item.id)];

    writeWishlist(nextItems);
    syncWishlistUi();
  }

  function updateWishlistToggles(items) {
    const savedIds = new Set(items.map((item) => item.id));

    document.querySelectorAll("[data-wishlist-toggle]").forEach((button) => {
      const isActive = savedIds.has(button.dataset.productId);
      const label = isActive ? "Retirer des favoris" : "Ajouter aux favoris";

      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", String(isActive));
      button.setAttribute("aria-label", label);
      button.querySelectorAll("[data-wishlist-label]").forEach((labelNode) => {
        labelNode.textContent = label;
      });
    });
  }

  function renderWishlistCard(item) {
    const article = document.createElement("article");
    article.className = "wishlist-card";

    const mediaLink = document.createElement("a");
    mediaLink.className = "wishlist-card__media";
    mediaLink.href = item.url;

    if (item.image) {
      const image = document.createElement("img");
      image.src = item.image;
      image.alt = item.title;
      image.loading = "lazy";
      mediaLink.appendChild(image);
    } else {
      const placeholder = document.createElement("span");
      placeholder.textContent = item.title.charAt(0);
      mediaLink.appendChild(placeholder);
    }

    const body = document.createElement("div");
    body.className = "wishlist-card__body";

    const title = document.createElement("h2");
    const titleLink = document.createElement("a");
    titleLink.href = item.url;
    titleLink.textContent = item.title;
    title.appendChild(titleLink);

    const price = document.createElement("p");
    price.className = "wishlist-card__price";
    price.textContent = item.price || "Voir le prix";

    const actions = document.createElement("div");
    actions.className = "wishlist-card__actions";

    const viewLink = document.createElement("a");
    viewLink.className = "btn btn--primary";
    viewLink.href = item.url;
    viewLink.textContent = "Voir le bouquet";

    const removeButton = document.createElement("button");
    removeButton.className = "wishlist-card__remove";
    removeButton.type = "button";
    removeButton.dataset.wishlistRemove = item.id;
    removeButton.textContent = "Retirer";

    actions.append(viewLink, removeButton);
    body.append(title, price, actions);
    article.append(mediaLink, body);

    return article;
  }

  function renderWishlistPage(items) {
    const page = document.querySelector("[data-wishlist-page]");
    if (!page) return;

    const grid = page.querySelector("[data-wishlist-grid]");
    const empty = page.querySelector("[data-wishlist-empty]");
    const count = page.querySelector("[data-wishlist-count]");
    const countLabel = `${items.length} bouquet${items.length > 1 ? "s" : ""}`;

    if (count) count.textContent = countLabel;
    if (!grid || !empty) return;

    grid.replaceChildren();
    grid.hidden = items.length === 0;
    empty.hidden = items.length > 0;

    items.forEach((item) => {
      grid.appendChild(renderWishlistCard(item));
    });
  }

  function syncWishlistUi() {
    const items = readWishlist();
    updateWishlistToggles(items);
    renderWishlistPage(items);
  }

  function initWishlist() {
    document.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const toggleButton = target.closest("[data-wishlist-toggle]");
      if (toggleButton) {
        const item = getWishlistButtonItem(toggleButton);
        if (!item) return;
        event.preventDefault();
        event.stopPropagation();
        toggleWishlistItem(item);
        return;
      }

      const removeButton = target.closest("[data-wishlist-remove]");
      if (removeButton) {
        event.preventDefault();
        removeWishlistItem(removeButton.dataset.wishlistRemove || "");
      }
    });

    document.addEventListener("shopify:section:load", syncWishlistUi);
    window.addEventListener("storage", (event) => {
      if (event.key === wishlistStorageKey) syncWishlistUi();
    });

    window.BouquetWishlist = {
      key: wishlistStorageKey,
      read: readWishlist,
      sync: syncWishlistUi,
    };

    syncWishlistUi();
  }

  // ── Preloader: spinning rose petals ──
  function initPreloader() {
    const mark = preloader?.querySelector(".preloader__mark");
    const textEl = preloader?.querySelector("[data-preloader-text] span");
    if (!mark || reduceMotion || coarsePointer || compactViewport) return;

    // Text cycling
    const messages = [
      "Chargement de votre espace",
      "Configuration de votre espace",
      "Ouverture imminente"
    ];
    let msgIndex = 0;
    if (textEl) {
      textEl.textContent = messages[0];
      const textInterval = setInterval(() => {
        msgIndex++;
        if (msgIndex < messages.length) {
          textEl.textContent = messages[msgIndex];
        } else {
          clearInterval(textInterval);
        }
      }, 800);
    }

    const canvas = document.createElement("canvas");
    canvas.classList.add("preloader__particles");
    canvas.width = 300;
    canvas.height = 300;
    canvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;";
    mark.appendChild(canvas);

    const ctx = canvas.getContext("2d");
    const W = 300, H = 300;
    const cx = W / 2, cy = H / 2;
    let tick = 0;

    // Petals orbiting
    const petals = [];
    for (let i = 0; i < 12; i++) {
      petals.push({
        baseAngle: (Math.PI * 2 * i) / 12,
        orbit: 50 + Math.random() * 70,
        size: 10 + Math.random() * 14,
        speed: 0.3 + Math.random() * 0.5,
        alpha: 0.35 + Math.random() * 0.45,
        flutter: Math.random() * 3,
      });
    }

    // Floating particles
    const particles = [];
    for (let i = 0; i < 30; i++) {
      particles.push({
        x: Math.random() * W,
        y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4 - 0.2,
        size: 1.5 + Math.random() * 3,
        alpha: 0.2 + Math.random() * 0.5,
        life: Math.random() * 200,
        maxLife: 200 + Math.random() * 200,
      });
    }

    function drawPetal(x, y, angle, size, alpha) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      // Petal shape: teardrop/ellipse with point
      ctx.beginPath();
      ctx.moveTo(0, -size);
      ctx.bezierCurveTo(size * 0.6, -size * 0.4, size * 0.6, size * 0.3, 0, size * 0.8);
      ctx.bezierCurveTo(-size * 0.6, size * 0.3, -size * 0.6, -size * 0.4, 0, -size);
      ctx.closePath();
      ctx.fillStyle = `rgba(199, 31, 50, ${alpha})`;
      ctx.fill();
      ctx.restore();
    }

    function draw() {
      if (preloader.classList.contains("is-hidden")) return;
      ctx.clearRect(0, 0, W, H);
      tick += 0.016;

      // Draw petals orbiting
      petals.forEach((p) => {
        const angle = p.baseAngle + tick * p.speed;
        const ox = cx + Math.cos(angle) * p.orbit;
        const oy = cy + Math.sin(angle) * p.orbit;
        const flutter = Math.sin(tick * 3 + p.flutter) * 0.3;
        drawPetal(ox, oy, angle + Math.PI / 2 + flutter, p.size, p.alpha);
      });

      // Draw floating particles
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.life++;
        if (p.life > p.maxLife || p.x < -20 || p.x > W + 20 || p.y < -20 || p.y > H + 20) {
          p.x = Math.random() * W;
          p.y = H + 20;
          p.life = 0;
        }
        const fade = p.life < 40 ? p.life / 40 : p.life > p.maxLife - 40 ? (p.maxLife - p.life) / 40 : 1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(199, 31, 50, ${p.alpha * fade})`;
        ctx.fill();
      });

      // Center glow
      const glow = ctx.createRadialGradient(cx, cy, 5, cx, cy, 45);
      glow.addColorStop(0, 'rgba(199, 31, 50, 0.15)');
      glow.addColorStop(1, 'rgba(199, 31, 50, 0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(cx, cy, 45, 0, Math.PI * 2);
      ctx.fill();

      requestAnimationFrame(draw);
    }

    draw();
  }

  // ── Preloader hide ──
  window.addEventListener("load", () => {
    window.setTimeout(() => preloader?.classList.add("is-hidden"), coarsePointer || compactViewport ? 350 : 900);
  });

  ["pointerdown", "touchstart", "wheel", "keydown"].forEach((eventName) => {
    window.addEventListener(eventName, () => preloader?.classList.add("is-hidden"), { once: true, passive: true });
  });

  initSmoothScroll();

  // ── Header scroll ──
  window.addEventListener("scroll", () => {
    header?.classList.toggle("is-scrolled", window.scrollY > 24);
  });

  function initBouquetBuilder() {
    document.querySelectorAll("[data-bouquet-builder]").forEach((builder) => {
      const choices = Array.from(builder.querySelectorAll("[data-builder-choice]"));
      const step = builder.querySelector("[data-builder-step]");
      const title = builder.querySelector("[data-builder-title]");

      choices.forEach((choice) => {
        choice.addEventListener("click", () => {
          choices.forEach((item) => item.classList.remove("is-active"));
          choice.classList.add("is-active");
          if (step) step.textContent = choice.dataset.step || "";
          if (title) title.textContent = choice.dataset.title || "";
        });
      });
    });
  }

  function initMotionTilt() {
    if (reduceMotion || !window.matchMedia("(pointer: fine)").matches) return;

    document.querySelectorAll(".motion-tilt").forEach((card) => {
      card.addEventListener("mousemove", (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        card.style.transform = `perspective(900px) rotateX(${y * -4}deg) rotateY(${x * 5}deg) translateY(-4px)`;
      });

      card.addEventListener("mouseleave", () => {
        card.style.transform = "";
      });
    });
  }

  // ── GSAP Animations ──
  if (window.gsap && !reduceMotion) {
    gsap.registerPlugin(window.ScrollTrigger);

    const revealItems = gsap.utils.toArray(".reveal");
    if (revealItems.length) {
      gsap.from(revealItems, {
        y: 28,
        opacity: 0,
        duration: 0.9,
        stagger: 0.14,
        ease: "power3.out",
        delay: 0.2,
      });
    }

    gsap.utils.toArray(".reveal-card").forEach((item) => {
      gsap.from(item, {
        y: 46,
        opacity: 0,
        duration: 0.9,
        ease: "power3.out",
        scrollTrigger: {
          trigger: item,
          start: "top 82%",
        },
      });
    });

    gsap.utils.toArray(".bouquet-builder__choice, .bouquet-care__step, .bouquet-trust__item").forEach((item, index) => {
      gsap.from(item, {
        y: 22,
        opacity: 0,
        duration: 0.7,
        delay: Math.min(index * 0.04, 0.18),
        ease: "power3.out",
        scrollTrigger: {
          trigger: item,
          start: "top 88%",
        },
      });
    });

    // ── Stack cards: magnet hover + float ──
    const stack = document.querySelector(".atelier__stack");
    const card1 = document.querySelector(".stack-card--one");
    const card2 = document.querySelector(".stack-card--two");
    const card3 = document.querySelector(".stack-card--three");

    // Idle floating animations
    let float1, float2, float3;
    function startFloat() {
      if (float1) float1.kill();
      if (float2) float2.kill();
      if (float3) float3.kill();
      float1 = gsap.to(card1, {
        y: -10,
        rotate: -2,
        duration: 3.0,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        overwrite: "auto",
      });
      float2 = gsap.to(card2, {
        y: -18,
        rotate: -8,
        duration: 3.6,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        overwrite: "auto",
      });
      float3 = gsap.to(card3, {
        y: 18,
        rotate: 10,
        duration: 4.2,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        overwrite: "auto",
      });
    }
    if (stack && card1 && card2 && card3) {
      startFloat();

      // Mouse enters: cards spread apart like repelling magnets
      stack.addEventListener("mouseenter", () => {
        float2.kill();
        float3.kill();
        gsap.to(card2, {
          x: -80,
          y: -40,
          rotate: -14,
          scale: 0.95,
          duration: 0.5,
          ease: "power2.out",
        });
        gsap.to(card3, {
          x: 80,
          y: 40,
          rotate: 14,
          scale: 0.95,
          duration: 0.5,
          ease: "power2.out",
        });
      });

      // Mouse leaves: cards return to center + resume float
      stack.addEventListener("mouseleave", () => {
        gsap.to([card2, card3], {
          x: 0,
          y: 0,
          rotate: 0,
          scale: 1,
          duration: 0.6,
          ease: "elastic.out(1, 0.6)",
          onComplete: () => {
            gsap.set(card2, { rotate: -6 });
            gsap.set(card3, { rotate: 8 });
            startFloat();
          },
        });
      });
    }

    // ── Hero roses float ──
    gsap.utils.toArray(".rose-photo").forEach((rose, index) => {
      gsap.to(rose, {
        y: index % 2 ? 22 : -20,
        x: index % 2 ? -14 : 12,
        rotate: index % 2 ? "-=5" : "+=5",
        duration: 4.6 + index * 0.45,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    });
  }

  initBouquetBuilder();
  initMotionTilt();
  initWishlist();

  // ── Init preloader ──
  initPreloader();

  // ── Clear flower scene canvas ──
  const flowerCanvas = document.getElementById("flower-scene");
  const flowerCtx = flowerCanvas?.getContext("2d");
  flowerCtx?.clearRect(0, 0, flowerCanvas.width, flowerCanvas.height);
})();
